import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Drawer, Input } from 'antd';
import { holdingEntityBackgroundColors } from '../../user/components/HoldingEntityList/background-colors/holding-entity-background-colors';
import { HoldingEntityContentWrapper, DrawerFooter, DrawerWrapper, EditHoldingEntityInputRow, FloatingLabeInInput, InputWithLabelOnTopWrapper, RequiredFieldIndicator } from './EditHoldingEntity.styles';
import { PrimaryButton, PrimaryButtonWithIcon, SecondaryButtonWithIcon } from '../../../common/components/buttons/NewButtons.styles';
import { ActionLockedIconWithTooltip } from '../../../common/components/ActionLocked/ActionLockedIconWithTooltip';
import { DeleteHoldingEntity } from '../../user/components/HoldingEntityList/DeleteHoldingEntity';
import { HoldingEntityColorPicker } from '../../user/components/HoldingEntityList/HoldingEntityColorPicker';
import { EraseHoldingEntitySvg, HoldingEntityMoreActions } from '../../user/components/HoldingEntityList/HoldingEntityMoreActions';
import { HoldingEntityModalHeader } from '../../user/components/HoldingEntityList/HoldingEntityModalHeader';
import { DefaultEntityAction } from '../../user/components/HoldingEntityList/DefaultEntityAction';
import { HoldingEntitySelect } from '../../user/components/HoldingEntityList/HoldingEntitySelect';
import { HOLDING_ENTITY_INDIVIDUAL_TYPES, HOLDING_ENTITY_NAME_MAX_LENGTH, HOLDING_ENTITY_TYPES } from '../utils/entities.consts';

import { useTrackEvent } from '../../../hooks/useTrackEvent/useTrackEvent';
import { BaseModal } from '../../../common/components/BaseModal/BaseModal';
import { useStore } from '../../app/data/root.store';

export const EditHoldingEntity = observer((props) => {
  const { uiStore } = useStore();

  useTrackEvent('Popup Viewed', {
    title: 'Holding entity modal opened',
    url: window.location.href
  })

  return (
    uiStore.isDesktopView ?
      ReactDOM.createPortal(<BaseModal width={'512px'} height={'auto'} borderRadius={8}>
        <EditHoldingEntityContent {...props} />
      </BaseModal>, document.body) :
      <Drawer
        closable={false}
        visible={props.drawerVisible !== false}
        width={'100%'}
        // height={'100%'}
        className={'editHoldingEntityDrawerContainer_mobile'}
        placement={'right'}
        destroyOnClose={true}
        push={false}
        onClose={() => { props.onClose() }} >
        <EditHoldingEntityContent {...props} isMobileView />
      </Drawer>
  )
})

export const EditHoldingEntityContent = observer(({
  isEdit = false,
  item = {},
  onSave,
  onDelete,
  onClose,
  isMobileView,
}) => {
  const { userStore } = useStore();

  const [holdingEntityData, setHoldingEntityData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isDelete, setIsDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const classNameInputRef = useRef(null);

  const isBlockedByRoleOrPaymentStatus = userStore.isReadOnlyMode || userStore.isActionBlockedByRole('addHoldingEntity') || userStore.isActionBlockedByRole('editHoldingEntity');

  useEffect(() => {
    if (classNameInputRef.current) {
      setTimeout(() => {
        classNameInputRef.current.focus();
      }, 100);
    }
  }, []);

  useEffect(() => {
    setHoldingEntityData({
      name: item.name || '',
      description: item.description || '',
      colorCode: holdingEntityBackgroundColors.find((bgColor) => bgColor === item.colorCode) ? item.colorCode : holdingEntityBackgroundColors[0],
      isDefault: item.isDefault || false,
      holdingEntityType: item.type,
      itemCount: item?.items?.length || 0,
      relationshipToPrimaryBeneficiary: item.relationshipToPrimaryBeneficiary
    });
  }, [item]);


  const fieldChange = (field, value) => {
    if (formErrors[field]) {
      resetFormInputError(field);
    }
    setHoldingEntityData({ ...holdingEntityData, [field]: value })
  }

  const updateFormErrors = (data) => {
    const errors = {};
    const fields = [
      { name: 'name', message: 'Name is required' },
    ];

    fields.forEach(field => {
      const value = data[field.name];
      if (!value || !value?.trim()) {
        errors[field.name] = field.message;
      }
    });

    setFormErrors(errors);
    return errors;
  }

  const resetFormInputError = (input) => {
    delete formErrors[input];
  }

  const safeTrim = (str) => str ? str.trim() : '';


  const handleSave = async (e) => {
    if (isBlockedByRoleOrPaymentStatus) {
      userStore.setShowLimitedSharedAccess(true);
    } else {
      setIsSaving(true);
      const trimmedHoldingEntityData = {
        ...holdingEntityData,
        name: safeTrim(holdingEntityData.name),
      };

      const errors = updateFormErrors(trimmedHoldingEntityData);
      const isFormValid = Object.keys(errors).length === 0;
      if (isFormValid) {
        await onSave?.({
          ...trimmedHoldingEntityData,
          type: trimmedHoldingEntityData.holdingEntityType,
          ...(isEdit ? { id: item.id } : {})
        });
      }
      setIsSaving(false);
    }
  }

  const mobileViewDeleteHoldingEntity = () => {
    if (userStore.isDemoOrReadOnlyMode || userStore.isActionBlockedByRole('deleteHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
    } else {
      setIsDelete(true);
    }
  }

  return (
    <DrawerWrapper>
      <HoldingEntityModalHeader
        isEdit={isEdit}
        onClose={onClose}
        isMobileView={isMobileView}
        holdingEntityData={holdingEntityData}
      />

      <HoldingEntityContentWrapper>
        <EditHoldingEntityInputRow>
          <InputWithLabelOnTopWrapper withError={formErrors.name}>
            <FloatingLabeInInput>Name</FloatingLabeInInput>
            {
              !!formErrors.name &&
              <RequiredFieldIndicator>Required</RequiredFieldIndicator>
            }
            <Input style={{ height: '64px' }} ref={classNameInputRef} value={holdingEntityData.name} onChange={(e) => { fieldChange('name', e.target.value) }} maxLength={HOLDING_ENTITY_NAME_MAX_LENGTH} />
          </InputWithLabelOnTopWrapper>
        </EditHoldingEntityInputRow>

        <HoldingEntitySelect
          onTypeChange={(type) => fieldChange('holdingEntityType', type)}
          formErrors={formErrors}
          options={HOLDING_ENTITY_TYPES}
          title='Type'
          placeholder='Select type'
          value={holdingEntityData.holdingEntityType}
        />
        {
          holdingEntityData.holdingEntityType === 'Individual' && (
            <HoldingEntitySelect
              onTypeChange={(val) => fieldChange('relationshipToPrimaryBeneficiary', val)}
              formErrors={formErrors}
              options={HOLDING_ENTITY_INDIVIDUAL_TYPES}
              title='Relationship to primary'
              placeholder='Select relationship'
              value={holdingEntityData.relationshipToPrimaryBeneficiary}
            />
          )
        }

        <EditHoldingEntityInputRow>
          <InputWithLabelOnTopWrapper withError={formErrors.description}>
            <FloatingLabeInInput>Description</FloatingLabeInInput>
            <Input.TextArea autoSize style={{ height: '64px' }} maxLength={500} value={holdingEntityData.description} onChange={(e) => { fieldChange('description', e.target.value) }} />
          </InputWithLabelOnTopWrapper>
        </EditHoldingEntityInputRow>

        <HoldingEntityColorPicker
          holdingEntityData={holdingEntityData}
          onChangeColor={(color) => fieldChange('colorCode', color)}
          isMobileView={isMobileView}
        />

        {
          !isMobileView &&
          <HoldingEntityMoreActions
            isEdit={isEdit}
            holdingEntityData={holdingEntityData}
            onDefaultEntityChange={(checked) => fieldChange('isDefault', checked)}
            mobileViewDeleteHoldingEntity={mobileViewDeleteHoldingEntity}
          />
        }

        {!isMobileView ? <DrawerFooter>
          <PrimaryButton
            onClick={handleSave}
            mobileHeight={56}
            mobileBorderRadius={12}
            disabled={isSaving}
            style={{ width: '100%' }}
          >
            {isBlockedByRoleOrPaymentStatus && <ActionLockedIconWithTooltip iconColor={'#fff'} marginLeft={'0px'} isRoleBlocked={userStore.isActionBlockedByRole('addHoldingEntity') || userStore.isActionBlockedByRole('editHoldingEntity')} />}
            {isEdit ? 'Save' : 'Create'}
          </PrimaryButton>
        </DrawerFooter> :
          <DefaultEntityAction holdingEntityData={holdingEntityData} onDefaultEntityChange={(checked) => fieldChange('isDefault', checked)} />
        }

      </HoldingEntityContentWrapper>
      {isMobileView && <DrawerFooter>
        <div style={{ flex: 1 }} />
        {
          isEdit && isMobileView &&
          <SecondaryButtonWithIcon onClick={mobileViewDeleteHoldingEntity} style={{ color: '#ff0000' }} mobileHeight={56} mobileBorderRadius={12}>
            {
              (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('deleteHoldingEntity')) ?
                <ActionLockedIconWithTooltip iconColor={'#ff0000'} marginLeft={'0px'} isRoleBlocked={userStore.isActionBlockedByRole('deleteHoldingEntity')} />
                :
                <EraseHoldingEntitySvg />
            }
            Delete holding entity</SecondaryButtonWithIcon>
        }
        <PrimaryButtonWithIcon onClick={handleSave} mobileHeight={56} mobileBorderRadius={12} disabled={isSaving}>
          {
            isBlockedByRoleOrPaymentStatus &&
            <ActionLockedIconWithTooltip
              iconColor={'#fff'}
              marginLeft={'0px'}
              isRoleBlocked={(userStore.isActionBlockedByRole('addHoldingEntity') || userStore.isActionBlockedByRole('editHoldingEntity'))}
            />
          }
          {isEdit ? 'Save' : 'Create'}
        </PrimaryButtonWithIcon>
      </DrawerFooter>}
      {
        isDelete &&
        <DeleteHoldingEntity
          item={item}
          onClose={() => setIsDelete(false)}
          onDelete={() => { onDelete(item.id); }}
          relevantImage={holdingEntityBackgroundColors.find((bgColor) => bgColor === holdingEntityData.colorCode)}
        />
      }
    </DrawerWrapper>
  );
})
